import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { SERVICES } from "./data";
import { scrollToSection } from "@/lib/scroll";

export const Services = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="servizi" data-testid="services-section" className="py-24 sm:py-32 lg:py-40 bg-[#F3F0EA] border-y border-[#E5E2DC]">
      <div className="px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-4">Servizi</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] text-[#181817] max-w-2xl">
            Cosa possiamo fare per te
          </h2>
        </Reveal>
        <div className="mt-16 grid lg:grid-cols-[1.5fr_1fr] gap-12 lg:gap-20">
          <Reveal delay={0.1}>
            {SERVICES.map((s, i) => (
              <div key={s.num} className="border-b border-[#C5C2BA]">
                <button
                  data-testid={`service-toggle-${s.num}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center gap-6 py-7 text-left group"
                >
                  <span className="font-mono text-xs tracking-[0.2em] text-[#C85A32]">{s.num}</span>
                  <span className="font-display text-2xl sm:text-3xl font-light text-[#181817]">{s.title}</span>
                  <motion.span
                    animate={{ rotate: open === i ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="ml-auto text-[#6E6D69] group-hover:text-[#181817]"
                  >
                    <Plus size={20} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      data-testid={`service-detail-${s.num}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="text-base text-[#181817]/80 leading-relaxed max-w-xl">{s.description}</p>
                      <ul className="mt-6 mb-8 grid sm:grid-cols-2 gap-3">
                        {s.deliverables.map((d) => (
                          <li key={d} className="flex items-start gap-3 text-sm text-[#181817]/80">
                            <Check size={15} className="mt-0.5 text-[#4A5446] shrink-0" />
                            {d}
                          </li>
                        ))}
                      </ul>
                      {s.b2b && (
                        <div className="mb-8 max-w-xl">
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C85A32]">
                            Per aziende e privati
                          </p>
                          <button
                            data-testid="b2b-quote-button"
                            onClick={() => window.dispatchEvent(new Event("open-b2b-modal"))}
                            className="group mt-4 flex items-center gap-2 bg-[#181817] text-[#FBF9F5] px-6 py-3 rounded-full text-sm hover:bg-[#C85A32] transition-colors duration-300"
                          >
                            Richiedi preventivo
                            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </button>
                          <p className="mt-3 text-xs text-[#6E6D69]">
                            Compila i dati e invia la richiesta: ti rispondiamo su WhatsApp.
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.2}>
            <div className="lg:sticky lg:top-28 bg-[#181817] text-[#FBF9F5] rounded-sm p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C85A32]">Preventivo gratuito</p>
              <h3 className="mt-4 font-display text-3xl font-light leading-snug">Ti serve una multifunzione?</h3>
              <p className="mt-4 text-sm text-[#FBF9F5]/70 leading-relaxed">
                Chiamaci o scrivici su WhatsApp: preventivo gratuito e senza impegno, con sopralluogo in sede su richiesta.
              </p>
              <button
                data-testid="services-cta-button"
                onClick={() => window.dispatchEvent(new Event("open-quote-modal"))}
                className="group mt-8 flex items-center gap-2 bg-[#FBF9F5] text-[#181817] px-6 py-3 rounded-full text-sm hover:bg-[#C85A32] hover:text-[#FBF9F5] transition-colors duration-300"
              >
                Richiedi preventivo
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
            <div data-testid="services-pc-cta" className="mt-6 bg-[#F3F0EA] border border-[#E5E2DC] rounded-sm p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C85A32]">PC & Periferiche · Novità</p>
              <h3 className="mt-4 font-display text-2xl font-light leading-snug text-[#181817]">Ti servono postazioni PC complete?</h3>
              <p className="mt-3 text-sm text-[#181817]/70 leading-relaxed">
                Contattaci su WhatsApp o tramite mail per un preventivo gratuito. Per aziende e privati.
              </p>
              <button
                data-testid="services-pc-cta-button"
                onClick={() => window.dispatchEvent(new Event("open-b2b-modal"))}
                className="group mt-6 flex items-center gap-2 bg-[#181817] text-[#FBF9F5] px-6 py-3 rounded-full text-sm hover:bg-[#C85A32] transition-colors duration-300"
              >
                Richiedi preventivo
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <a
                data-testid="services-pc-mail-link"
                href="mailto:info@furlanprintcopy.it?subject=Preventivo%20postazioni%20PC"
                className="mt-4 inline-block text-xs text-[#6E6D69] underline underline-offset-4 hover:text-[#181817] transition-colors duration-300"
              >
                oppure scrivici a info@furlanprintcopy.it
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
